import { type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { css } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { iconSizes } from "@stubhub/broadway-types";
import { PriceTagFillIcon } from "@stubhub/broadway-web/icons/functional";
import { ToastA11Y, ToastKind, ToastTiming, useToast } from "@viagogo/core/ui";
import { useFeatureParticipationRecorder, useFeatureTernary, useSimpleFeatureTernary } from "@viagogo/feature";
import { useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { CookiePreferencesContext } from "src/shared/context/cookie-preferences-context";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { isCobrandFullAIP, useIsStubhubNoram, useIsUSA } from "src/shared/utils/appContextHelper";
import { useShowGoogleSignInPrompt } from "src/website-apps/shared/components/google-signin-prompt/hooks/useShowGoogleSignInPrompt";
import { useWelcomeToastEligibility } from "src/website-apps/shared/hooks/useWelcomeToastEligibility";
import {
  setBottomSheetOpenAtom,
  isConfirmLocationModalNotVisibleAtom,
} from "src/website-apps/shared/utils/confirmLocationVisibilityAtom";
import { calendarFilterDrawerOpenAtom, filterDrawerOpenAtom } from "src/website-apps/shared/utils/filterDrawerOpenAtom";
import { useAipBottomsheetFrequency } from "./useAipBottomsheetFrequency";

const toastIconContainer = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingBlock: "primitives.100",
  paddingInline: "primitives.100",
  color: "primitives.neutral.100",
});

const AipToastIcon = () => (
  <div className={toastIconContainer}>
    <PriceTagFillIcon size={iconSizes[3]} />
  </div>
);

export interface AipBottomsheetState {
  isOpen: boolean;
  titleContent: ReactNode;
  onOpenChange: (isOpen: boolean) => void;
  copyVariant: string;
}

/**
 * Manages toasts and overlay notifications for performer pages.
 * All experimentation and priority logic is encapsulated here.
 *
 * Notification priority (only ONE shows per page view):
 * 1. WelcomeToast (fires from ViagogoApp.tsx)
 * 2. AIP Bottomsheet (variant + not frequency-capped within 24h)
 * 3. All-In Pricing toast (lowest, fallback)
 *
 * On mobile, Google Sign-In prompt blocks AIP until dismissed/completed.
 * On tablet/desktop, AIP shows alongside the Google Sign-In prompt.
 */
export const usePerformerToasts = (): {
  aipBottomsheet: AipBottomsheetState | null;
  isReturningGoogleUser: boolean | null;
} => {
  const { enqueueToast, closeToastWithFade } = useToast();
  const hasFiredRef = useRef(false);
  const aipToastKeyRef = useRef<string | null>(null);
  const isConfirmLocationModalNotVisible = useAtomValue(isConfirmLocationModalNotVisibleAtom);
  const isFilterDrawerOpen = useAtomValue(filterDrawerOpenAtom);
  const isCalendarFilterDrawerOpen = useAtomValue(calendarFilterDrawerOpenAtom);

  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const { hasSelectedPreferences } = useContext(CookiePreferencesContext);
  const { shouldDisplayWelcomeToastControl, isNewGoogleUserResolved } = useWelcomeToastEligibility(
    appContext.isUserLoggedIn,
    appContext.appName,
  );

  /* --- ALL-IN PRICING TOAST --- */
  const isUsAip = useIsUSA() && appContext.ipCountryCode === "US";
  const isInternationalAip = isCobrandFullAIP(appContext);
  // CWP-2879: SH NorAm AIP copy experiment — control keeps the legacy CMS copy,
  // variant shows "No hidden fees". Non-SH-NorAm domains always keep the legacy copy.
  // isShNoram covers SH_US and SH_CA, but AIP surfaces only fire for SH_US (isUsAip) or
  // international full-AIP cobrands today, so SH_CA users never see the copy.
  const isShNoram = useIsStubhubNoram();
  const canParticipateCopyExperiment = useCallback(() => isShNoram, [isShNoram]);
  // record: false — participation is only recorded when an AIP surface using the copy is actually
  // shown (see recordAipCopyParticipation below). Recording at render time would count every
  // SH NorAm performer-page view, including users who never see an AIP surface (no cookie consent,
  // welcome toast, frequency cap), diluting the experiment metrics.
  const isNoHiddenFeesCopyVariant =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerAIPNoHiddenFeesCopy,
      false,
      true,
      canParticipateCopyExperiment,
      false,
    ) ?? false;
  const recordAipCopyParticipation = useFeatureParticipationRecorder(
    FeatureSetEnum.TMPerformerAIPNoHiddenFeesCopy,
    canParticipateCopyExperiment,
  );
  const aipToastContentLegacy = useContent("cid$#3909025#"); // "Prices include all fees"
  const aipToastContentVariant = useContent("cid$#4407028#"); // "No hidden fees"
  const aipContentVariant1 = isNoHiddenFeesCopyVariant ? aipToastContentVariant : aipToastContentLegacy;
  const aipCopyVariant = isNoHiddenFeesCopyVariant ? "no_hidden_fees" : "prices_include_all_fees";
  const canParticipateUsAipExperiment = useCallback(() => isUsAip, [isUsAip]);

  const fireAipToast = useCallback(
    (content: typeof aipContentVariant1) => {
      // Toast onClose fires on every close path (icon, fade, drawer); only toasts
      // actually shown get a dismissal event.
      let impressed = false;
      const key = enqueueToast({
        icon: AipToastIcon,
        content,
        kind: ToastKind.Brand,
        a11y: ToastA11Y.passive,
        timing: ToastTiming.long,
        showCloseIcon: true,
        onClose: () => {
          if (impressed) {
            storePageEvent(PE_TYPE.CLOSE, PE_KEYS.AIP_TOAST_DISMISSED, aipCopyVariant);
          }
        },
      });
      // If a filter drawer was already open when the toast fired (e.g. user opened
      // filters while the Google Sign-In gate was still blocking), dismiss immediately.
      if (isFilterDrawerOpen || isCalendarFilterDrawerOpen) {
        closeToastWithFade(key);
      } else {
        impressed = true;
        aipToastKeyRef.current = key;
        recordAipCopyParticipation();
        storePageEvent(PE_TYPE.OPEN, PE_KEYS.AIP_TOAST_IMPRESSION, aipCopyVariant);
      }
    },
    [
      enqueueToast,
      isFilterDrawerOpen,
      isCalendarFilterDrawerOpen,
      closeToastWithFade,
      storePageEvent,
      aipCopyVariant,
      recordAipCopyParticipation,
    ],
  );
  /* --- --- */

  /* --- GOOGLE SIGN-IN PROMPT SEQUENCING --- */
  const { googleSignInCompleted, isReturningGoogleUser, isReturningGoogleUserResolved } = useShowGoogleSignInPrompt({
    featureFlag: FeatureSetEnum.TMPerformerShowGoogleSignInPrompt,
    location: "Performer",
  });
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  // On tablet/desktop, show AIP alongside Google prompt (no sequencing needed)
  const isGoogleGateSatisfied = !isMobile || googleSignInCompleted;

  const canParticipateInternationalAipRerun = useCallback(
    () => isMobile && isInternationalAip,
    [isMobile, isInternationalAip],
  );
  const execInternationalAipRerun = useFeatureTernary(
    FeatureSetEnum.MPerformerAIPToastV2IntlRerun,
    canParticipateInternationalAipRerun,
  );
  const canParticipateInternationalAipTabletRerun = useCallback(() => isInternationalAip, [isInternationalAip]);
  const execInternationalAipTabletRerun = useFeatureTernary(
    FeatureSetEnum.TPerformerAIPToastV2IntlRerun,
    canParticipateInternationalAipTabletRerun,
  );

  const setBottomSheetOpen = useSetAtom(setBottomSheetOpenAtom);
  useEffect(() => {
    setBottomSheetOpen({
      id: "performer-google-signin-prompt",
      isOpen: !isGoogleGateSatisfied,
    });

    return () => {
      setBottomSheetOpen({
        id: "performer-google-signin-prompt",
        isOpen: false,
      });
    };
  }, [isGoogleGateSatisfied, setBottomSheetOpen]);
  /* --- --- */

  /* --- AIP BOTTOMSHEET --- */
  const { shouldShowBottomsheet: notFreqCapped, isFrequencyResolved, markShown } = useAipBottomsheetFrequency();
  const bottomsheetTitleV1 = useContent("cid$#4167497#"); // "What you see is what you pay"
  const bottomsheetTitleV2Legacy = useContent("cid$#4167498#"); // "Prices include all fees"
  const bottomsheetTitleV2Variant = useContent("cid$#4407029#"); // "No hidden fees"
  const bottomsheetTitleV2 = isNoHiddenFeesCopyVariant ? bottomsheetTitleV2Variant : bottomsheetTitleV2Legacy;
  const execBottomsheetMobile = useFeatureTernary(
    FeatureSetEnum.MPerformerAIPBottomsheet,
    canParticipateUsAipExperiment,
  );

  const canParticipateReturningUserExperiment = useCallback(
    () => (isUsAip || isInternationalAip) && appContext.isUserLoggedIn,
    [isUsAip, isInternationalAip, appContext.isUserLoggedIn],
  );
  const shouldSuppressBottomsheet =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerAIPReturningUser,
      false,
      true,
      canParticipateReturningUserExperiment,
    ) ?? false;

  const [bottomsheetState, setBottomsheetState] = useState<{ title: ReactNode; copyVariant: string } | null>(null);

  const handleBottomsheetOpenChange = useCallback((open: boolean) => {
    if (!open) setBottomsheetState(null);
  }, []);
  /* --- --- */

  useEffect(() => {
    if (
      hasFiredRef.current ||
      !isConfirmLocationModalNotVisible ||
      !isGoogleGateSatisfied ||
      !hasSelectedPreferences ||
      !isFrequencyResolved ||
      !isReturningGoogleUserResolved ||
      !isNewGoogleUserResolved
    ) {
      return;
    }
    hasFiredRef.current = true;

    // Priority 1: WelcomeToast (fires from ViagogoApp.tsx — just check, don't fire)
    if (isReturningGoogleUser === true) {
      return;
    }
    if (isReturningGoogleUser === null && shouldDisplayWelcomeToastControl) {
      return;
    }
    // isReturningGoogleUser === false (new user): fall through to AIP

    const isInternationalAipRerunVariant =
      execInternationalAipRerun({
        control: () => false,
        variant: () => true,
      }) ?? false;
    const isInternationalAipTabletRerunVariant =
      execInternationalAipTabletRerun({
        control: () => false,
        variant: () => true,
      }) ?? false;

    // Priority 2: AIP Bottomsheet (mobile experiment + desktop/tablet productionized)
    // The telemetry label must reflect the title actually rendered. MPerformerAIPBottomsheet variant 1
    // shows "What you see is what you pay" rather than the copy experiment's control/variant copy, so
    // it carries its own label instead of inheriting the copy-experiment label.
    const bottomsheetMobileResolution = isInternationalAipTabletRerunVariant
      ? { title: bottomsheetTitleV2, copyVariant: aipCopyVariant }
      : execBottomsheetMobile({
          control: () => null,
          variant: (_data, variantId) =>
            variantId === 1
              ? { title: bottomsheetTitleV1, copyVariant: "what_you_see_is_what_you_pay" }
              : { title: bottomsheetTitleV2, copyVariant: aipCopyVariant },
        });
    const bottomsheetTitleDesktop = !isMobile && isUsAip ? bottomsheetTitleV2 : null;
    const resolvedBottomsheetTitle = bottomsheetMobileResolution?.title ?? bottomsheetTitleDesktop;
    const resolvedCopyVariant = bottomsheetMobileResolution?.copyVariant ?? aipCopyVariant;
    // Rerun variant is toast-only — the sheet must never render for this arm.
    if (!isInternationalAipRerunVariant && resolvedBottomsheetTitle && notFreqCapped && !shouldSuppressBottomsheet) {
      setBottomsheetState({
        title: resolvedBottomsheetTitle,
        copyVariant: resolvedCopyVariant,
      });
      // MPerformerAIPBottomsheet variant 1 shows its own title ("What you see is what you pay") and does
      // not use the copy experiment's copy, so only record participation when the resolved copy is the
      // copy-experiment variant.
      if (resolvedCopyVariant === aipCopyVariant) {
        recordAipCopyParticipation();
      }
      markShown();
      return;
    }

    // Priority 3: AIP toast (fallback)
    const aipToastContent =
      isUsAip || isInternationalAipRerunVariant || isInternationalAipTabletRerunVariant ? aipContentVariant1 : null;
    if (aipToastContent) {
      fireAipToast(aipToastContent);
    }
  }, [
    isConfirmLocationModalNotVisible,
    isGoogleGateSatisfied,
    hasSelectedPreferences,
    isFrequencyResolved,
    isReturningGoogleUser,
    isReturningGoogleUserResolved,
    isNewGoogleUserResolved,
    shouldDisplayWelcomeToastControl,
    execInternationalAipRerun,
    execInternationalAipTabletRerun,
    execBottomsheetMobile,
    shouldSuppressBottomsheet,
    notFreqCapped,
    markShown,
    bottomsheetTitleV1,
    bottomsheetTitleV2,
    isMobile,
    isUsAip,
    fireAipToast,
    aipContentVariant1,
    aipCopyVariant,
    recordAipCopyParticipation,
  ]);

  // Dismiss the AIP toast when any filter drawer opens (CXWPE-3916)
  useEffect(() => {
    if (!((isFilterDrawerOpen || isCalendarFilterDrawerOpen) && aipToastKeyRef.current)) {
      return;
    }

    closeToastWithFade(aipToastKeyRef.current);
    aipToastKeyRef.current = null;
  }, [isFilterDrawerOpen, isCalendarFilterDrawerOpen, closeToastWithFade]);

  return {
    aipBottomsheet: bottomsheetState
      ? {
          isOpen: true,
          titleContent: bottomsheetState.title,
          onOpenChange: handleBottomsheetOpenChange,
          copyVariant: bottomsheetState.copyVariant,
        }
      : null,
    isReturningGoogleUser,
  };
};
